/* eslint no-undef: "error" */

import React, { useState, useEffect } from "react";
import { object, array, string, number } from "prop-types";
import assetify from "@quintype/framework/assetify";
import { connect } from "react-redux";
import get from "lodash/get";

import StoryTitleInfo from "../../molecules/StoryTitleInfo";
import StoryContent from "../../molecules/StoryContent";
import StoryCard from "../../story-templates/StoryCard";
import StoryHeroImage from "../../atoms/StoryHeroImage";
import StoryPageMetaData from "../../molecules/StoryPageMetaData";
import ScrollSnap from "../../atoms/ScrollSnap";
import LeftArrowButton from "../../atoms/icons/left-arrow-button";
import RightArrowButton from "../../atoms/icons/right-arrow-button";
import CloseIcon from "../../atoms/icons/close";
import LightBox from "../../molecules/LightBox";
import DfpAd from "../../dfp-ad";
import DefaultImage from "../../atoms/CardImage/default.svg";
import FullScreenIcon from "../../../../assets/icons/full-screen-icon.png";

import { toBanglaNum } from "../../utils/utils";
import { staticTexts } from "../../utils/Translate";
import { useM3widget } from "../../utils/react-utils";

import "./blank.m.css";

const BlankStoryTemplate = ({
  story,
  summary,
  relatedStories,
  imageElements,
  startingCard,
  lang,
  jwtToken,
  scrollIndex,
  domainSlug,
  numberOfTotalStories,
}) => {
  const [showCarousel, setShowCarousel] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const slicedStoryCards = story.cards.slice(startingCard);
  const [isMobile, setIsMobile] = useState(false);
  const sponsorId = get(story, ["metadata", "story-attributes", "sponsoredby", "0", "id"], null);
  const selectionChange = (index) => setSelectedIndex(index);

  const closeEvent = (e) => {
    setShowCarousel(!showCarousel);

    exitFullscreenWindow();
  };

  const getCarouselIndex = (element) => {
    return imageElements.findIndex((item) => item.id === element.id);
  };

  const carouselEvent = (e, element) => {
    setShowCarousel(!showCarousel);
    setCarouselIndex(getCarouselIndex(element));
  };

  const exitFullscreenWindow = () => {
    if (document.fullscreenElement) {
      document
        .exitFullscreen()
        .then(() => {})
        .catch((err) => console.error(err));
    }
  };

  const makeFullScreen = () =>
    document.fullscreenElement ? exitFullscreenWindow() : document.documentElement.requestFullscreen();

  const children =
    imageElements && imageElements.length > 0
      ? imageElements.map((item, index) => <LightBox element={item} isCarousel={true} key={index} />)
      : null;

  useEffect(() => {
    global.deviceWidth < 876 && setIsMobile(!isMobile);
  }, []);

  let sectionId = "";
  if (get(story, ["sections", "0", "parent-id"]) === null) {
    sectionId = get(story, ["sections", "0", "id"], null);
  } else {
    sectionId = get(story, ["sections", "0", "parent-id"], null);
  }
  const m3widgets = useM3widget(sectionId);

  return (
    <div styleName="wrapper" className={`story-------${scrollIndex}`}>
      <div styleName="ad-slot" className="top-ad">
        <DfpAd
          adtype={lang === "en" ? "EnPalo_ROS_728x90_T1" : "News_728x90_T1"}
          targetStory={story}
          slotId={`story_${scrollIndex}-t1`}
        />
      </div>
      <div styleName="story-first-part" className="print-story-first-part">
        <div>
          <div styleName="meta-content" className="print-meta-content">
            <StoryTitleInfo story={story} summary={summary} />
            <StoryPageMetaData story={story} lang={lang} />
            {story["story-template"] !== "photo" && story["hero-image-s3-key"] && (
              <StoryHeroImage story={story} imageWidth={isMobile ? [375] : [900]} />
            )}
            {showCarousel && (
              <div styleName="scroll-snap">
                <ScrollSnap
                  leftArrow={
                    <LeftArrowButton
                      color={isMobile ? "#fff0" : "#ffffff66"}
                      color2={isMobile ? "#ffffff" : "#000000"}
                    />
                  }
                  rightArrow={
                    <RightArrowButton
                      color={isMobile ? "#fff0" : "#ffffff66"}
                      color2={isMobile ? "#ffffff" : "#000000"}
                    />
                  }
                  isIndicator={!isMobile}
                  defaultSelection={carouselIndex}
                  selectionChange={selectionChange}
                >
                  {children}
                </ScrollSnap>
                <div styleName="icons">
                  <span styleName="image-index">{`${
                    lang === "en" ? selectedIndex + 1 : toBanglaNum(selectedIndex + 1)
                  } / ${lang === "en" ? imageElements.length : toBanglaNum(imageElements.length)}`}</span>
                  <img
                    src={`${assetify(FullScreenIcon)}`}
                    alt="full-screen-icon close icon"
                    styleName="full-screen-icon"
                    onClick={makeFullScreen}
                  ></img>
                  <span onClick={() => closeEvent()} className="close">
                    <CloseIcon color="#e2e2e2" />
                  </span>
                </div>
              </div>
            )}
            {slicedStoryCards.length > 0 &&
              slicedStoryCards.slice(0, 1).map((card, index) => (
                <div key={card.id} id={card.id} styleName="first-story-card">
                  <StoryCard
                    card={card}
                    story={story}
                    onCarouselEvent={carouselEvent}
                    lang={lang}
                    domainSlug={domainSlug}
                    imageElements={imageElements}
                  />
                </div>
              ))}
          </div>
        </div>
        <div>
          <div styleName={`adslot-r1`}>
            {((isMobile && sponsorId === null) || !isMobile) && (
              <>
                <div styleName="advertising">{staticTexts(lang).advertising}</div>
                <DfpAd
                  adtype={lang === "en" ? "EnPalo_ROS_336x280_R1" : "News_336x280_R1"}
                  targetStory={story}
                  slotId={`story_${scrollIndex}-r1`}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <StoryContent
        story={story}
        slicedStoryCards={slicedStoryCards.slice(1, story.length)}
        relatedStories={relatedStories}
        imageElements={imageElements}
        showMetadata={false}
        isMobile={isMobile}
        jwtToken={jwtToken}
        scrollIndex={scrollIndex}
        sponsorId={sponsorId}
        lang={lang}
      />

      {m3widgets && <div className="colombiaone" data-slot="446167" id="clmb_div_446167"></div>}
      {scrollIndex < numberOfTotalStories - 1 && (
        <div className="sun-logo">
          <img src={`${assetify(DefaultImage)}`} alt="sun" />
        </div>
      )}
    </div>
  );
};

const BlankStory = ({
  story,
  relatedStories,
  summary,
  imageElements,
  startingCard,
  lang,
  jwtToken,
  scrollIndex,
  domainSlug,
  numberOfTotalStories,
}) => {
  return (
    <div className="story-grid">
      <BlankStoryTemplate
        story={story}
        relatedStories={relatedStories}
        summary={summary}
        imageElements={imageElements}
        startingCard={startingCard}
        lang={lang}
        jwtToken={jwtToken}
        scrollIndex={scrollIndex}
        domainSlug={domainSlug}
        numberOfTotalStories={numberOfTotalStories}
      />
    </div>
  );
};

BlankStoryTemplate.propTypes = {
  story: object,
  relatedStories: array,
  summary: string,
  imageElements: array,
  startingCard: number,
  lang: string,
  jwtToken: string,
  scrollIndex: number,
  domainSlug: string,
  numberOfTotalStories: number,
};

BlankStory.propTypes = {
  story: object,
  relatedStories: array,
  summary: string,
  imageElements: array,
  startingCard: number,
  lang: string,
  jwtToken: string,
  scrollIndex: number,
  domainSlug: string,
  numberOfTotalStories: number,
};

const mapStateToProps = (state) => ({
  lang: get(state, ["qt", "config", "publisher-attributes", "lang"], ""),
  jwtToken: get(state, ["userReducer", "jwt_token"], null),
  domainSlug: get(state, ["qt", "domainSlug"]),
});

export default connect(mapStateToProps, null)(BlankStory);
